/**
 * A GIF of the year, yours or the chat's: the GIF itself playing in a tilted
 * frame, a count sticker slammed onto its corner, and a line about it.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

import { prefersReducedMotion } from "../motion";
import { Scene, Zone, useStoryPaused, type BlobSpec } from "../story/stage";
import { useTimeline } from "../story/timeline";
import type { GifCard } from "../types";
import { count } from "./shared";

const FRAME_TOP = 470;
const FRAME_ROOM = { width: 860, height: 820 };

/** The frame's size: the GIF's own shape, as big as fits the room. */
export function gifSize(gif: GifCard, room = FRAME_ROOM) {
  const ratio = gif.width > 0 && gif.height > 0 ? gif.width / gif.height : 16 / 9;
  let width = room.width;
  let height = width / ratio;
  if (height > room.height) {
    height = room.height;
    width = height * ratio;
  }
  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * The GIF playing: the copy this server kept, or its source if that's missing.
 * Muted and inline, so phones let it play on its own; it pauses with the story.
 */
export function GifVideo({ gif, label }: { gif: GifCard; label: string }) {
  const kept = `/api/wrapped/media/${gif.id}`;
  const [src, setSrc] = useState(kept);
  const video = useRef<HTMLVideoElement>(null);
  const paused = useStoryPaused();
  const still = prefersReducedMotion();

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    // React doesn't write `muted` as an attribute, and iPhones only autoplay a video that has it.
    el.muted = true;
    el.setAttribute("muted", "");
    if (paused || still) el.pause();
    else void el.play()?.catch(() => {});
  }, [paused, still, src]);

  return (
    <video
      ref={video}
      src={src}
      aria-label={label}
      loop
      muted
      playsInline
      preload="auto"
      onError={() => setSrc((was) => (was === kept ? gif.source : was))}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
    />
  );
}

export function GifSpotlight({
  gif,
  kicker,
  lead,
  line,
  blobs,
}: {
  gif: GifCard;
  kicker: string;
  lead: string;
  line: ReactNode;
  blobs: BlobSpec[];
}) {
  const { width, height } = gifSize(gif);
  const left = Math.round(540 - width / 2);
  const top = FRAME_TOP + Math.round((FRAME_ROOM.height - height) / 2);

  const root = useTimeline((s) => {
    s.rise(".kick", 0.35, { y: 0 });
    s.rise("[data-part=lead]", 0.45, { y: 30, sound: false });
    // The frame spins in and lands at a slight tilt, like a sticker on a page.
    s.tl.fromTo(".wr-gif", { scale: 0.2, rotation: -24, autoAlpha: 0 }, { scale: 1, rotation: -3, autoAlpha: 1, duration: 0.85, ease: "back.out(1.6)" }, 0.6);
    s.sound("whoosh", 0.6, 1.2, { length: 0.5 });
    s.sound("pop", 1.15, 0.8);
    // Then the count slams onto its corner and knocks the frame.
    s.slam(".gif-badge", 1.5, { from: 3.2 });
    s.shake(".gif-frame", 1.8, 7, 14);
    s.rise("[data-part=line]", 2.1, { y: 24 });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={blobs}>
        <Zone at="top">
          <div className="kick">{kicker}</div>
          <div data-part="lead" className="lead">
            {lead}
          </div>
        </Zone>
        <Zone at="middle">
          <div className="gif-frame abs" style={{ left, top, width, height }}>
            <div className="wr-gif" style={{ width, height, transform: "rotate(-3deg)" }}>
              <GifVideo gif={gif} label={kicker} />
            </div>
            <div className="gif-badge" style={{ left: width - 150, top: -110 }}>
              <b className="disp">×{count(gif.count)}</b>
              <span className="mono">SENT</span>
            </div>
          </div>
        </Zone>
        <Zone at="bottom">
          <div data-part="line" className="line" style={{ top: 1520 }}>
            {line}
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
